import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { LoginPage } from "../../components/admin-panel/login/login-page";
import { SignUpPage } from "../../components/admin-panel/signup/signup-page";
import { RouteName } from "./routes.helper";
import NotFound from "../../components/not-found/not-found.page";
import LandingPage from "../../components/landing-page/landing-page";
import ProtectedRoute from "../../components/protected-route/protected-route";
import BookingSuccess from "../../components/booking-page/success/booking-success";
import BookingFailure from "../../components/booking-page/failure/booking-failure";
import BookingError from "../../components/booking-page/error/booking-error";
import BookingCheckIn from "../../components/booking-page/check-in/booking-check-in";

const Dashboard = lazy(() => import("../../components/admin-panel/dashboard/dashboard").then(m => ({ default: m.Dashboard })));
const AlertsPage = lazy(() => import("../../components/admin-panel/alerts/alerts.page").then(m => ({ default: m.AlertsPage })));
const BookingList = lazy(() => import("../../components/admin-panel/bookings/booking-list"));
const ShowList = lazy(() => import("../../components/admin-panel/shows/show-list"));
const SettingsList = lazy(() => import("../../components/admin-panel/settings/settings-list"));
const TheaterList = lazy(() => import("../../components/admin-panel/theaters/theater-list"));
const UsersList = lazy(() => import("../../components/admin-panel/users/users-list"));
const ContentList = lazy(() => import("../../components/admin-panel/content/content-list"));
const ImportBookingsPage = lazy(() => import("../../components/admin-panel/import-bookings/import-bookings.page"));
const LogViewer = lazy(() => import("../../components/admin-panel/logs/log-viewer"));
const AtlasBackupPage = lazy(() => import("../../components/admin-panel/backup/atlas-backup.page"));
const BannerSettings = lazy(() => import("../../components/admin-panel/banner-settings/banner-settings"));
const VolcanoBannerSettings = lazy(() => import("../../components/admin-panel/volcano-banner-settings/volcano-banner-settings"));
const PromoCodeList = lazy(() => import("../../components/admin-panel/promocodes/promocode-list"));

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: RouteName.LANDING,
    element: <LandingPage />,
  },
  {
    path: RouteName.BOOKING_SUCCESS,
    element: <BookingSuccess />,
  },
  {
    path: RouteName.BOOKING_FAILURE,
    element: <BookingFailure />,
  },
  {
    path: RouteName.BOOKING_ERROR,
    element: <BookingError />,
  },
  {
    path: RouteName.BOOKING_CHECK_IN,
    element: <BookingCheckIn />,
  },
  {
    path: RouteName.LOGIN,
    element: <LoginPage />,
  },
  ...(process.env.NODE_ENV !== 'production' ? [{
    path: RouteName.SIGNUP,
    element: <SignUpPage />,
  }] : []),
  {
    element: <ProtectedRoute />,
    path: RouteName.ADMIN,
    children: [
      {
        path: RouteName.DASHBOARD,
        element: <Lazy><Dashboard /></Lazy>,
      },
      {
        path: RouteName.ALERTS,
        element: <Lazy><AlertsPage /></Lazy>,
      },
      {
        path: RouteName.BOOKINGS,
        element: <Lazy><BookingList /></Lazy>,
      },
      {
        path: RouteName.THEATERS,
        element: <Lazy><TheaterList /></Lazy>,
      },
      {
        path: RouteName.CALENDAR,
        element: <Lazy><ShowList /></Lazy>,
      },
      {
        path: RouteName.USERS,
        element: <Lazy><UsersList /></Lazy>,
      },
      {
        path: RouteName.SETTINGS,
        element: <Lazy><SettingsList /></Lazy>,
      },
      {
        path: RouteName.CONTENT,
        element: <Lazy><ContentList /></Lazy>,
      },
      {
        path: RouteName.IMPORT_BOOKINGS,
        element: <Lazy><ImportBookingsPage /></Lazy>,
      },
      {
        path: RouteName.LOGS,
        element: <Lazy><LogViewer /></Lazy>,
      },
      {
        path: RouteName.BACKUP,
        element: <Lazy><AtlasBackupPage /></Lazy>,
      },
      {
        path: RouteName.BANNER_SETTINGS,
        element: <Lazy><BannerSettings /></Lazy>,
      },
      {
        path: RouteName.VOLCANO_BANNER_SETTINGS,
        element: <Lazy><VolcanoBannerSettings /></Lazy>,
      },
      {
        path: RouteName.PROMO_CODES,
        element: <Lazy><PromoCodeList /></Lazy>,
      },
    ],
  },
  {
    path: RouteName.WILDCARD,
    element: <NotFound />,
  },
]);
